<template>
  <div class="cc-page">
    <div class="cc-page__inner">
      <section class="cc-hero">
        <h1>{{ $t('convertCoordinates.title') }}</h1>
        <p>{{ $t('convertCoordinates.subtitle') }}</p>
      </section>

      <section class="cc-sheet">
        <header class="cc-sheet__head">
          <h2>{{ $t('convertCoordinates.inputTitle') }}</h2>
          <p>{{ $t('convertCoordinates.inputSubtitle') }}</p>
        </header>

        <div class="cc-sheet__body">
          <conversion-options
            :source-system="sourceSystem"
            :utm-zone="utmZone"
            :disabled="loading"
            @update:sourceSystem="sourceSystem = $event"
            @update:utmZone="utmZone = $event"
          />

          <div
            class="cc-tabs"
            role="tablist"
          >
            <button
              type="button"
              class="cc-tabs__btn"
              :class="{ 'is-active': tab === 'coordinates' }"
              role="tab"
              :aria-selected="tab === 'coordinates'"
              :disabled="loading"
              @click="setTab('coordinates')"
            >
              {{ $t('convertCoordinates.tabs.coordinates') }}
            </button>
            <button
              type="button"
              class="cc-tabs__btn"
              :class="{ 'is-active': tab === 'file' }"
              role="tab"
              :aria-selected="tab === 'file'"
              :disabled="loading"
              @click="setTab('file')"
            >
              {{ $t('convertCoordinates.tabs.file') }}
            </button>
          </div>

          <coordinate-input-table
            v-if="tab === 'coordinates'"
            :rows="rows"
            :source-system="sourceSystem"
            :utm-zone="utmZone"
            :invalid-indexes="invalidIndexes"
            :disabled="loading"
            @add-row="addRow"
            @remove-row="removeRow"
            @clear="clearRows"
            @paste="applyPaste"
          />

          <coordinate-file-dropzone
            v-else
            :file="file"
            :disabled="loading"
            @select="onSelectFile"
            @remove="file = null"
          />

          <div
            v-if="clientErrors.length"
            class="cc-alert cc-alert--danger"
          >
            <p
              v-for="(item, index) in clientErrors"
              :key="`client-${index}`"
            >
              {{ item }}
            </p>
          </div>

          <div
            v-if="error"
            class="cc-alert cc-alert--danger"
          >
            <p v-if="error.message">
              {{ error.message }}
            </p>
            <ul v-if="error.errors && error.errors.length">
              <li
                v-for="(item, index) in error.errors"
                :key="`api-${index}`"
              >
                {{ item }}
              </li>
            </ul>
          </div>

          <div class="cc-actions">
            <button
              type="button"
              class="cc-btn cc-btn--primary"
              :disabled="loading"
              @click="convert"
            >
              <b-spinner
                v-if="loading"
                small
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
                size="16"
              />
              {{ loading ? $t('convertCoordinates.actions.converting') : $t('convertCoordinates.actions.convert') }}
            </button>
          </div>
        </div>
      </section>

      <section
        v-if="hasResult"
        class="cc-summary"
        :class="summaryTone"
      >
        <span class="cc-chip">
          {{ $t('convertCoordinates.summary.total') }}
          <strong>{{ summary.total }}</strong>
        </span>
        <span class="cc-chip cc-chip--ok">
          {{ $t('convertCoordinates.summary.converted') }}
          <strong>{{ summary.converted }}</strong>
        </span>
        <span class="cc-chip cc-chip--warn">
          {{ $t('convertCoordinates.summary.failed') }}
          <strong>{{ summary.failed }}</strong>
        </span>
        <span
          v-if="source === 'file' && fileType"
          class="cc-chip"
        >
          {{ $t('convertCoordinates.summary.fileType') }}
          <strong>{{ fileType }}</strong>
        </span>
        <p
          v-if="isFailedOnly"
          class="cc-summary__note"
        >
          {{ $t('convertCoordinates.results.failedOnly') }}
        </p>
        <p
          v-else-if="isPartialSuccess"
          class="cc-summary__note"
        >
          {{ message || $t('convertCoordinates.results.partial') }}
        </p>
        <p
          v-else-if="isFullSuccess"
          class="cc-summary__note"
        >
          {{ message || $t('convertCoordinates.results.success') }}
        </p>
      </section>

      <failed-rows-alert :rows="failed" />

      <converted-results-table
        :rows="coordinates"
        :visible="hasResult"
        @copy-row="copyRow"
        @copy-all="copyAll"
        @download-csv="downloadAll"
      />

      <converted-points-map :points="mapPoints" />
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import useConvertCoordinates from '@/composables/useConvertCoordinates'
import { MAX_COORDINATES } from '@/api/coordinate-conversion/types'
import ConversionOptions from './components/ConversionOptions.vue'
import CoordinateInputTable from './components/CoordinateInputTable.vue'
import CoordinateFileDropzone from './components/CoordinateFileDropzone.vue'
import ConvertedResultsTable from './components/ConvertedResultsTable.vue'
import FailedRowsAlert from './components/FailedRowsAlert.vue'
import ConvertedPointsMap from './components/ConvertedPointsMap.vue'
import {
  buildJsonRequest,
  cloneEmptyRows,
  collectFilledRows,
  coordinatesToCsv,
  createEmptyRow,
  downloadCsv,
  parseClipboardRows,
  RESULT_CSV_KEYS,
  validateCoordinateRows,
  validateUploadFile,
  wgs84PointsFromConverted,
} from './utils/coordinateRows'

export default {
  name: 'ConvertCoordinatesPage',
  components: {
    BSpinner,
    ConversionOptions,
    CoordinateInputTable,
    CoordinateFileDropzone,
    ConvertedResultsTable,
    FailedRowsAlert,
    ConvertedPointsMap,
  },
  setup() {
    return useConvertCoordinates()
  },
  data() {
    return {
      tab: 'coordinates',
      sourceSystem: 'auto',
      utmZone: '36N',
      rows: cloneEmptyRows(1),
      file: null,
      clientErrors: [],
      invalidIndexes: [],
    }
  },
  computed: {
    mapPoints() {
      return wgs84PointsFromConverted(this.coordinates)
    },
    summaryTone() {
      if (this.isFailedOnly) return 'is-danger'
      if (this.isPartialSuccess) return 'is-warn'
      if (this.isFullSuccess) return 'is-ok'
      return ''
    },
  },
  watch: {
    sourceSystem() {
      this.invalidIndexes = []
    },
  },
  methods: {
    setTab(next) {
      if (next === this.tab) return
      this.tab = next
      this.clientErrors = []
      this.invalidIndexes = []
      this.reset()
      if (next === 'file') {
        this.rows = cloneEmptyRows(1)
      } else {
        this.file = null
      }
    },
    addRow() {
      if (this.rows.length >= MAX_COORDINATES) {
        this.$toast.error(this.$t('convertCoordinates.errors.tooMany'))
        return
      }
      this.rows.push(createEmptyRow())
    },
    removeRow(index) {
      if (this.rows.length === 1) return
      this.rows.splice(index, 1)
    },
    clearRows() {
      this.rows = cloneEmptyRows(1)
      this.invalidIndexes = []
      this.clientErrors = []
    },
    applyPaste(text) {
      const parsed = parseClipboardRows(text)
      if (!parsed.length) {
        this.$toast.error(this.$t('convertCoordinates.errors.pasteEmpty'))
        return
      }
      const filled = collectFilledRows(this.rows)
      const next = filled.concat(parsed).slice(0, MAX_COORDINATES)
      this.rows = next.length ? next : cloneEmptyRows(1)
      if (filled.length + parsed.length > MAX_COORDINATES) {
        this.$toast.error(this.$t('convertCoordinates.errors.tooMany'))
      }
    },
    onSelectFile(file) {
      const check = validateUploadFile(file)
      if (!check.ok) {
        this.clientErrors = [this.$t(`convertCoordinates.errors.${check.code}`)]
        this.file = null
        return
      }
      this.clientErrors = []
      this.file = file
    },
    validateClient() {
      this.clientErrors = []
      this.invalidIndexes = []

      if (this.tab === 'file') {
        if (collectFilledRows(this.rows).length) {
          this.clientErrors.push(this.$t('convertCoordinates.errors.xor'))
          return false
        }
        const check = validateUploadFile(this.file)
        if (!check.ok) {
          this.clientErrors.push(this.$t(`convertCoordinates.errors.${check.code}`))
          return false
        }
        return true
      }

      if (this.file) {
        this.clientErrors.push(this.$t('convertCoordinates.errors.xor'))
        return false
      }

      const check = validateCoordinateRows(this.rows, this.sourceSystem, this.utmZone)
      if (!check.ok) {
        this.invalidIndexes = check.invalidIndexes
        if (check.code === 'incomplete_rows') {
          this.clientErrors.push(this.$t('convertCoordinates.errors.incompleteRows', {
            rows: check.invalidIndexes.join(', '),
          }))
        } else {
          this.clientErrors.push(this.$t(`convertCoordinates.errors.${check.code}`))
        }
        return false
      }

      return true
    },
    async convert() {
      if (this.loading) return
      if (!this.validateClient()) return

      try {
        if (this.tab === 'file') {
          await this.convertFile({
            file: this.file,
            source_system: this.sourceSystem || 'auto',
            utm_zone: this.utmZone || undefined,
          })
        } else {
          await this.convertJson(buildJsonRequest(this.rows, this.sourceSystem, this.utmZone))
        }
        this.$nextTick(() => {
          const summary = this.$el && this.$el.querySelector('.cc-summary')
          if (summary && summary.scrollIntoView) {
            summary.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      } catch (err) {
        // API error is already stored on `error` from the composable.
      }
    },
    copyText(text) {
      if (this.$copyText) {
        this.$copyText(text).then(() => {
          this.$toast.success(this.$t('convertCoordinates.actions.copied'))
        }).catch(() => {
          this.$toast.error(this.$t('convertCoordinates.errors.copyFailed'))
        })
        return
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$toast.success(this.$t('convertCoordinates.actions.copied'))
        }).catch(() => {
          this.$toast.error(this.$t('convertCoordinates.errors.copyFailed'))
        })
      }
    },
    copyRow(row) {
      this.copyText(coordinatesToCsv([row]))
    },
    copyAll() {
      this.copyText(coordinatesToCsv(this.coordinates))
    },
    downloadAll() {
      downloadCsv('converted-coordinates.csv', coordinatesToCsv(this.coordinates, RESULT_CSV_KEYS))
    },
  },
}
</script>

<style lang="scss">
.cc-page {
  background: #e8eef5;
  margin: -1.2rem -1.2rem 0;
  padding: 16px 12px 80px;
  min-height: calc(100vh - 80px);
}

.cc-page__inner {
  max-width: 1180px;
  margin: 0 auto;
}

.cc-hero {
  background: #06284d;
  border-top: 8px solid #5cc10b;
  border-radius: 8px;
  padding: 18px 20px 16px;
  margin-bottom: 12px;
  color: #fff;
}

.cc-hero h1 {
  margin: 0 0 4px;
  font-size: 1.35rem;
  font-weight: 600;
}

.cc-hero p {
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: 0.92rem;
}

.cc-sheet {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(6, 40, 77, 0.12), 0 1px 3px 1px rgba(6, 40, 77, 0.08);
  margin-bottom: 12px;
  overflow: hidden;
}

.cc-sheet--failed {
  border-top: 6px solid #d93025;
}

.cc-sheet__head {
  padding: 12px 16px 10px;
  border-bottom: 1px solid rgba(6, 40, 77, 0.1);
}

.cc-sheet__head h2 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #06284d;
}

.cc-sheet__head p {
  margin: 2px 0 0;
  color: #5f6368;
  font-size: 0.8rem;
}

.cc-sheet__head--row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.cc-sheet__body {
  padding: 14px 16px 16px;
}

.cc-options {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 14px;
}

.cc-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #06284d;
  margin-bottom: 4px;
}

.cc-input {
  width: 100%;
  height: 38px;
  border: 1px solid #c5ced8;
  border-radius: 6px;
  padding: 0 10px;
  color: #06284d;
  background: #fff;
}

.cc-input:focus {
  outline: 0;
  border-color: #5cc10b;
  box-shadow: 0 0 0 3px rgba(92, 193, 11, 0.16);
}

.cc-input--table {
  height: 32px;
  padding: 0 6px;
  border-radius: 4px;
  font-size: 0.85rem;
}

.cc-hint {
  margin: 6px 0 0;
  color: #5f6368;
  font-size: 0.78rem;
}

.cc-tabs {
  display: flex;
  width: fit-content;
  max-width: 100%;
  background: #eef3f8;
  border-radius: 8px;
  padding: 4px;
  margin-bottom: 14px;
}

.cc-tabs__btn {
  border: 0;
  background: transparent;
  color: #06284d;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
}

.cc-tabs__btn.is-active {
  background: #fff;
  box-shadow: 0 1px 2px rgba(6, 40, 77, 0.12);
}

.cc-toolbar,
.cc-pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.cc-pager {
  margin: 12px 0 0;
}

.cc-toolbar__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.cc-count {
  font-size: 0.8rem;
  color: #5f6368;
}

.cc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid transparent;
  min-height: 36px;
  padding: 6px 12px;
  font-weight: 600;
  font-size: 0.88rem;
}

.cc-btn--ghost {
  background: #fff;
  border-color: #c5ced8;
  color: #06284d;
}

.cc-btn--primary {
  background: #06284d;
  color: #fff;
  min-width: 140px;
}

.cc-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cc-icon-btn {
  width: 30px;
  height: 30px;
  border: 0;
  background: transparent;
  color: #5f6368;
  border-radius: 6px;
}

.cc-icon-btn:hover:not(:disabled) {
  background: #eef3f8;
  color: #06284d;
}

.cc-table-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.cc-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

.cc-table th,
.cc-table td {
  padding: 6px;
  border-bottom: 1px solid rgba(6, 40, 77, 0.08);
  vertical-align: middle;
  text-align: start;
}

.cc-table thead th {
  font-size: 0.75rem;
  color: #06284d;
  background: #f4f7fb;
  white-space: nowrap;
}

.cc-subhead th {
  font-weight: 500;
  color: #5f6368;
}

.cc-th-index,
.cc-td-index,
.cc-th-action,
.cc-td-action {
  width: 36px;
  text-align: center;
}

.cc-table tbody tr.is-invalid {
  background: #fff2f1;
}

.cc-table--results {
  min-width: 980px;
}

.cc-table--results td {
  font-size: 0.82rem;
  white-space: nowrap;
}

.cc-dms,
.cc-raw {
  white-space: normal !important;
  max-width: 280px;
  word-break: break-word;
}

.cc-dropzone {
  border: 1.5px dashed #9aa8b8;
  border-radius: 10px;
  padding: 28px 16px;
  text-align: center;
  background: #f8fafc;
  cursor: pointer;
}

.cc-dropzone.is-over,
.cc-dropzone:hover {
  border-color: #5cc10b;
  background: #f4faec;
}

.cc-dropzone__input {
  display: none;
}

.cc-dropzone__icon {
  color: #06284d;
  margin-bottom: 8px;
}

.cc-dropzone__title {
  margin: 0;
  font-weight: 700;
  color: #06284d;
}

.cc-dropzone__sub {
  margin: 4px 0 12px;
  color: #5f6368;
  font-size: 0.85rem;
}

.cc-dropzone__btn {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  background: #06284d;
  color: #fff;
  font-size: 0.85rem;
}

.cc-file {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f4f7fb;
}

.cc-file__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cc-file__meta strong,
.cc-file__meta span {
  display: block;
}

.cc-file__meta span {
  color: #5f6368;
  font-size: 0.78rem;
}

.cc-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.cc-alert {
  margin-top: 12px;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 0.88rem;
}

.cc-alert--danger,
.cc-inline-error {
  background: #fff2f1;
  color: #b42318;
}

.cc-alert p,
.cc-alert ul {
  margin: 0;
}

.cc-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}

.cc-summary.is-ok {
  border-inline-start: 6px solid #5cc10b;
}

.cc-summary.is-warn {
  border-inline-start: 6px solid #b54708;
}

.cc-summary.is-danger {
  border-inline-start: 6px solid #d93025;
}

.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef3f8;
  color: #06284d;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.8rem;
}

.cc-chip--ok {
  background: #e8f6d8;
}

.cc-chip--warn {
  background: #fde7d6;
}

.cc-summary__note {
  width: 100%;
  margin: 4px 0 0;
  color: #5f6368;
  font-size: 0.85rem;
}

.cc-empty {
  padding: 20px 16px;
  color: #5f6368;
  text-align: center;
}

.cc-map {
  height: 360px;
  width: 100%;
  background: #e8eef5;
}

.cc-inline-error {
  margin: 0 16px 10px;
  padding: 8px 10px;
  border-radius: 6px;
}

@media (max-width: 767.98px) {
  .cc-page {
    padding: 12px 8px 96px;
  }

  .cc-hero h1 {
    font-size: 1.15rem;
  }

  .cc-options,
  .cc-sheet__head--row {
    grid-template-columns: 1fr;
    display: block;
  }

  .cc-options__field + .cc-options__field {
    margin-top: 10px;
  }

  .cc-tabs {
    width: 100%;
  }

  .cc-tabs {
    display: flex;
  }

  .cc-tabs__btn {
    flex: 1;
    text-align: center;
  }

  .cc-actions {
    position: sticky;
    bottom: 8px;
    justify-content: stretch;
    z-index: 3;
  }

  .cc-btn--primary {
    width: 100%;
  }

  .cc-map {
    height: min(48vh, 300px);
  }
}
</style>
